import type { IntlShape } from 'react-intl';
import type { RangeSliderFilterConfig } from '@alltrails/denali/components/FilterBar';

// v2 sliders drop the min/max end labels; the current range/value shows top-right via formatHeaderValue.
export const withoutMarkLabels = (marks: { value: number }[]) => marks.map(({ value }) => ({ value }));

/*
 * Whenever the upper thumb is at the max, revamp range sliders show the full range with the unit + "+"
 * only on the upper bound (e.g. "0–50 mi+", "6–50 mi+") rather than the hook's lower-bound-only "6 mi+".
 * `useAnyAsDefault` sliders (e.g. Estimated time) keep their "Any" text in the default full-range state.
 */
export const buildRevampRangeSlider = (
  rangeSlider: RangeSliderFilterConfig['rangeSlider'],
  intl: IntlShape,
  { useAnyAsDefault = false }: { useAnyAsDefault?: boolean } = {}
): RangeSliderFilterConfig['rangeSlider'] => {
  const { formatHeaderValue, marks } = rangeSlider;
  const minMark = marks[0];
  const maxMark = marks[marks.length - 1];

  return {
    ...rangeSlider,
    marks: withoutMarkLabels(marks),
    formatHeaderValue: ([lower, upper]) => {
      const isDefault = lower === minMark.value && upper === maxMark.value;
      // Keep the config's own default text (e.g. "Any") for useAnyAsDefault sliders in the full-range state.
      if (isDefault && useAnyAsDefault) {
        return formatHeaderValue?.([lower, upper]);
      }
      // Upper thumb at the max: show the range with the max's unit + "+" (e.g. "6–50 mi+").
      if (upper === maxMark.value) {
        return (
          <>
            {intl.formatNumber(lower)}–{maxMark.label}
          </>
        );
      }
      // Otherwise defer to the hook's formatting (e.g. "6–20 mi").
      return formatHeaderValue?.([lower, upper]);
    }
  };
};
